<script setup lang="ts">
const props = defineProps<{
  config?: {
    videoUrl?: string;
  };
  class?: string;
}>();

const validVideoId = (value: string | null | undefined) =>
  value && /^[A-Za-z0-9_-]{11}$/.test(value) ? value : "";

const getYouTubeVideoId = (value: unknown) => {
  const input = String(value || "").trim();
  if (!input) return "";

  const directId = validVideoId(input);
  if (directId) return directId;

  try {
    const url = new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`);
    const host = url.hostname.toLowerCase().replace(/^(www\.|m\.|music\.)/, "");

    if (host === "youtu.be") {
      return validVideoId(url.pathname.split("/").filter(Boolean)[0]);
    }

    if (host !== "youtube.com" && host !== "youtube-nocookie.com") return "";
    if (url.pathname === "/watch") return validVideoId(url.searchParams.get("v"));

    const parts = url.pathname.split("/").filter(Boolean);
    return ["embed", "shorts", "live", "v"].includes(parts[0] || "")
      ? validVideoId(parts[1])
      : "";
  } catch {
    return "";
  }
};

const videoId = computed(() => getYouTubeVideoId(props.config?.videoUrl));
const embedUrl = computed(() => videoId.value
  ? `https://www.youtube.com/embed/${videoId.value}`
  : "");
</script>

<template>
  <div v-if="embedUrl" :class="props.class || ''">
    <div class="youtube-video__frame">
      <iframe
        loading="lazy"
        :src="embedUrl"
        title="YouTube video"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      />
    </div>
  </div>
</template>

<style scoped>
.youtube-video__frame {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 0.75rem;
  background: #111;
}

.youtube-video__frame iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: auto;
}
</style>
